/**
 * XState Transcription State Machine
 *
 * 数学的モデル:
 * - 有限状態機械 M = (S, E, δ, s₀, F)
 * - S = {idle, connecting, recording, processing, completed, error}
 * - E = {START_RECORDING, STOP_RECORDING, CHUNK_SENT, TRANSCRIPTION_RECEIVED, ERROR}
 * - δ: S × E → S (状態遷移関数)
 * - s₀ = idle (初期状態)
 * - F = {completed, error} (終了状態)
 *
 * 不変条件:
 * - I₁: recording状態では必ずconnectionIdが存在
 * - I₂: chunksは時系列順で正規化されている
 * - I₃: completed状態では必ずtranscriptが存在
 * - I₄: error状態では必ずerrorメッセージが存在
 */

import { setup, assign } from 'xstate';
import { logStateTransition } from '@/lib/xstate-inspector';

// コンテキスト型定義
export interface TranscriptionContext {
  connectionId: string | null;
  meetingId: string | null;
  chunks: Blob[];
  transcript: string;
  error: Error | null;
  isRecording: boolean;
}

// イベント型定義
export type TranscriptionEvent =
  | { type: 'START_RECORDING' }
  | { type: 'STOP_RECORDING' }
  | { type: 'CONNECTED'; connectionId: string; meetingId: string }
  | { type: 'CONNECTION_FAILED'; error: Error }
  | { type: 'CHUNK_SENT'; chunk: Blob }
  | { type: 'TRANSCRIPTION_RECEIVED'; text: string }
  | { type: 'FORMAT_REQUESTED' }
  | { type: 'FORMAT_COMPLETED'; formattedText: string }
  | { type: 'FINAL_REQUESTED' }
  | { type: 'RESET' }
  | { type: 'ERROR'; error: Error };

// 文字起こしマシンの定義
export const transcriptionMachine = setup({
  types: {
    context: {} as TranscriptionContext,
    events: {} as TranscriptionEvent,
  },
  guards: {
    hasConnection: ({ context }) => context.connectionId !== null,
    hasTranscript: ({ context }) => context.transcript.length > 0,
    isRecording: ({ context }) => context.isRecording === true,
  },
  actions: {
    logTransition: ({ event }, params?: { from: string; to: string }) => {
      if (process.env.NODE_ENV !== 'test' && params?.from && params?.to) {
        logStateTransition('transcriptionMachine', params.from, params.to, event);
      }
    },
    logChunkSent: ({ context }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[transcriptionMachine] Chunk sent, total chunks:', context.chunks.length);
      }
    },
    logTranscriptionReceived: ({ context }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[transcriptionMachine] Transcription received, length:', context.transcript.length);
      }
    },
  }
}).createMachine({
  id: 'transcription',
  initial: 'idle',
  context: () => ({
    connectionId: null,
    meetingId: null,
    chunks: [],
    transcript: '',
    error: null,
    isRecording: false,
  }),
  states: {
    idle: {
      entry: assign({
        connectionId: () => null,
        meetingId: () => null,
        chunks: () => [],
        transcript: () => '',
        error: () => null,
        isRecording: () => false,
      }),
      on: {
        START_RECORDING: {
          target: 'connecting',
          actions: [
            assign({
              error: () => null,
            }),
            'logTransition'
          ]
        }
      }
    },

    connecting: {
      entry: assign({
        isRecording: () => false,
      }),
      on: {
        CONNECTED: {
          target: 'connected',
          actions: [
            assign({
              connectionId: ({ event }) => event.connectionId,
              meetingId: ({ event }) => event.meetingId,
            }),
            'logTransition'
          ]
        },
        CONNECTION_FAILED: {
          target: 'error',
          actions: assign({
            error: ({ event }) => event.error,
          })
        },
        STOP_RECORDING: {
          target: 'idle',
          actions: 'logTransition'
        },
        ERROR: {
          target: 'error',
          actions: assign({
            error: ({ event }) => event.error,
          })
        }
      }
    },

    connected: {
      entry: assign({ isRecording: () => false }),
      after: {
        100: 'recording'
      },
      on: {
        STOP_RECORDING: {
          target: 'idle',
          actions: 'logTransition'
        },
        ERROR: {
          target: 'error',
          actions: assign({ error: ({ event }) => event.error })
        }
      }
    },

    recording: {
      entry: assign({
        isRecording: () => true,
      }),
      on: {
        CHUNK_SENT: {
          actions: [
            assign({
              chunks: ({ context, event }) => [...context.chunks, event.chunk],
            }),
            'logChunkSent'
          ]
        },
        TRANSCRIPTION_RECEIVED: {
          actions: [
            assign({
              transcript: ({ event }) => event.text,
            }),
            'logTranscriptionReceived'
          ]
        },
        STOP_RECORDING: {
          target: 'processing',
          actions: [
            assign({
              isRecording: () => false,
            }),
            'logTransition'
          ]
        },
        ERROR: {
          target: 'error',
          actions: assign({
            error: ({ event }) => event.error,
            isRecording: () => false,
          })
        }
      }
    },

    processing: {
      entry: assign({
        isRecording: () => false,
      }),
      on: {
        FORMAT_REQUESTED: {
          // processing状態のまま、フォーマット処理待ち
          actions: () => {
            if (process.env.NODE_ENV === 'development') {
              console.log('[transcriptionMachine] Format requested');
            }
          }
        },
        FINAL_REQUESTED: {
          // S3保存要求後、完了状態に遷移
          target: 'completed',
          actions: () => {
            if (process.env.NODE_ENV === 'development') {
              console.log('[transcriptionMachine] Final requested');
            }
          }
        },
        FORMAT_COMPLETED: {
          target: 'completed',
          actions: [
            assign({
              transcript: ({ event }) => event.formattedText,
            }),
            'logTransition'
          ]
        },
        TRANSCRIPTION_RECEIVED: {
          // 処理中も文字起こし結果は受け取る
          actions: [
            assign({
              transcript: ({ event }) => event.text,
            }),
            'logTranscriptionReceived'
          ]
        },
        ERROR: {
          target: 'error',
          actions: assign({
            error: ({ event }) => event.error,
          })
        },
        // フォーマット不要で直接完了する場合
        STOP_RECORDING: {
          target: 'completed',
          guard: 'hasTranscript',
          actions: 'logTransition'
        }
      }
    },

    completed: {
      entry: assign({
        isRecording: () => false,
      }),
      on: {
        TRANSCRIPTION_RECEIVED: {
          actions: [
            assign({
              transcript: ({ event }) => event.text,
            }),
            'logTranscriptionReceived'
          ]
        },
        RESET: {
          target: 'idle',
          actions: 'logTransition'
        },
        START_RECORDING: {
          target: 'connecting',
          actions: 'logTransition'
        }
      }
    },

    error: {
      entry: assign({
        isRecording: () => false,
      }),
      on: {
        RESET: {
          target: 'idle',
          actions: [
            assign({
              error: () => null,
            }),
            'logTransition'
          ]
        },
        START_RECORDING: {
          target: 'connecting',
          actions: [
            assign({
              error: () => null,
            }),
            'logTransition'
          ]
        }
      }
    }
  }
});

// 状態の型エクスポート
export type TranscriptionMachine = typeof transcriptionMachine;
export type TranscriptionState =
  | 'idle'
  | 'connecting'
  | 'recording'
  | 'processing'
  | 'completed'
  | 'error';

// 状態検証関数
export function validateTranscriptionState(state: TranscriptionState): boolean {
  const validStates: TranscriptionState[] = [
    'idle',
    'connecting',
    'recording',
    'processing',
    'completed',
    'error'
  ];
  return validStates.includes(state);
}
